import { useState, useEffect } from 'react';
import { FiShield } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa6';
import { Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import CurrencyInput from '../../molecules/CurrencyInput';
import PageFooter from '../../molecules/PageFooter';
import { formatCpfCnpj, formatDate, formatCurrency } from '../../../utils/collateralFormatters';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import { INVENTORY_TYPE_OPTIONS } from '../../../types/Inventory';
import useInventoryPageList from '../../../hooks/useInventoryPageList';

const columns = [
    { id: 'description', title: 'Descrição / Nome', field: 'description', canSort: true, width: '220px' },
    { id: 'inventoryType', title: 'Tipo', field: 'inventoryType', canSort: false, width: '140px', cssClass: 'text-center' },
    { id: 'legalRegime', title: 'Regime', field: 'legalRegime', canSort: false, width: '150px' },
    { id: 'certifiedValue', title: 'Valor certificado', field: 'certifiedValue', canSort: false, width: '140px', cssClass: 'text-right' },
    { id: 'custodyCity', title: 'Depósito', field: 'custodyCity', canSort: true, width: '160px' },
    { id: 'custodyState', title: 'UF', field: 'custodyState', canSort: true, width: '60px', cssClass: 'text-center' },
    { id: 'statusFormatted', title: 'Situação', field: 'statusFormatted', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'createdAt', title: 'Cadastro', field: 'createdAt', canSort: true, width: '90px', cssClass: 'text-center' },
];

const InventoryAssetPageList = () => {
    const navigate = useNavigate();
    const {
        rows, total, page, setPage, pageSize, setPageSize,
        isLoading, initialLoading, filterCounts,
        searchInput, setSearchInput, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        inventoryType, setInventoryType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedInventory, inventoryDetail, isLoadingDetail, closePanel,
        apiInfo,
    } = useInventoryPageList();

    const handleTypeFilter = (value: string) => { setPage(1); setInventoryType(value); };
    const handleArchivedFilter = (value: string) => { setPage(1); setArchived(value); };
    const handleClearFilters = () => { setPage(1); setInventoryType(''); setArchived('false'); };

    const [showDischargeForm, setShowDischargeForm] = useState(false);
    const [dischargeDate, setDischargeDate] = useState('');
    const [dischargeValue, setDischargeValue] = useState(0);
    const [dischargeSubmitting, setDischargeSubmitting] = useState(false);

    const resetDischargeForm = () => {
        setShowDischargeForm(false);
        setDischargeDate('');
        setDischargeValue(0);
    };

    useEffect(() => {
        setShowDischargeForm(false);
        setDischargeDate('');
        setDischargeValue(0);
    }, [selectedInventory?.id]);

    const handleDischarge = async () => {
        if (!inventoryDetail) return;
        setDischargeSubmitting(true);
        try {
            await collateralApi.dischargeInventory(inventoryDetail.id, {
                discharge_date: dischargeDate,
                discharge_value: dischargeValue || undefined,
            });
            toast.success('Baixa registrada com sucesso');
            resetDischargeForm();
            closePanel();
            refresh();
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 422) { toast.error(e.data?.message || 'Informe a data de baixa'); return; }
                if (e.status === 409) { toast.error(e.data?.message || 'Estoque não está apto à baixa'); return; }
            }
            toast.error('Erro ao registrar baixa');
        } finally {
            setDischargeSubmitting(false);
        }
    };

    const typeCounts = filterCounts?.inventoryType || {};
    const totalAll = Object.values(typeCounts).reduce((a: number, b: any) => a + (b?.count || 0), 0);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={handleClearFilters}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!inventoryType && archived === 'false'
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{totalAll}</span>
                        </div>
                    </button>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">TIPO</div>
                        <div className="space-y-1">
                            {INVENTORY_TYPE_OPTIONS.map(({ value, label }) => (
                                <button key={value} onClick={() => handleTypeFilter(value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${inventoryType === value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <span>{typeCounts[value]?.label || label}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{typeCounts[value]?.count || 0}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">SITUAÇÃO</div>
                        <div className="space-y-1">
                            <button onClick={() => handleArchivedFilter('false')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${archived === 'false'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                Ativos
                            </button>
                            <button onClick={() => handleArchivedFilter('true')}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${archived === 'true'
                                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                Arquivados
                            </button>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const renderRightPanel = () => {
        if (isLoadingDetail) return <div className="p-4 text-gray-500 dark:text-gray-400">Carregando...</div>;
        if (!inventoryDetail) return <div className="p-4 text-gray-500 dark:text-gray-400">Selecione um estoque</div>;

        const d = inventoryDetail;
        return (
            <div className="p-4 space-y-4 overflow-auto h-full">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{d.name || d.description}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {d.inventoryTypeFormatted} • {d.custody?.city || '—'} • {d.custody?.state || '—'}
                    </p>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dados do Estoque</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500 dark:text-gray-400">Tipo:</span> <span className="dark:text-white">{d.inventoryTypeFormatted}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Regime:</span> <span className="dark:text-white">{d.legalRegimeFormatted || '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Quantidade:</span> <span className="dark:text-white">{d.quantityDescription || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Valor certificado:</span> <span className="dark:text-white">{d.certifiedValue != null ? formatCurrency(d.certifiedValue) : '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Descrição:</span> <span className="dark:text-white">{d.description || '—'}</span></div>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Depósito</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Endereço:</span> <span className="dark:text-white">{d.custody?.address || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Cidade:</span> <span className="dark:text-white">{d.custody?.city || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">UF:</span> <span className="dark:text-white">{d.custody?.state || '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Matrícula:</span> <span className="dark:text-white">{d.registrationNumber || '—'}</span></div>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Fiel depositário / Certificado</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Fiel depositário:</span> <span className="dark:text-white">{d.trusteeName || '—'}</span></div>
                        <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">CPF/CNPJ:</span> <span className="dark:text-white">{d.trusteeDocument ? formatCpfCnpj(d.trusteeDocument) : '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Certificado:</span> <span className="dark:text-white">{d.certificateId || '—'}</span></div>
                        <div><span className="text-gray-500 dark:text-gray-400">Data:</span> <span className="dark:text-white">{d.certificateDate ? formatDate(d.certificateDate) : '—'}</span></div>
                    </div>
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Proprietários</h4>
                    {d.owners && d.owners.length > 0 ? (
                        d.owners.map((o: any, i: number) => (
                            <div key={o.personId || o.document || i} className="text-sm">
                                <div className="dark:text-white">{o.name}</div>
                                {o.document && <div className="text-xs text-gray-500 dark:text-gray-400">{formatCpfCnpj(o.document)}</div>}
                            </div>
                        ))
                    ) : (<p className="text-sm text-gray-500 dark:text-gray-400">Nenhum proprietário</p>)}
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Obrigações</h4>
                    {d.obligations && d.obligations.length > 0 ? (
                        d.obligations.map((o: any, i: number) => (
                            <div key={o.creditOperationId || i} className="bg-gray-50 dark:bg-slate-700 rounded-lg p-2 text-sm">
                                <div className="dark:text-white font-medium">{o.creditOperationId}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {o.allocatedPercentage != null ? `${o.allocatedPercentage}%` : '—'} • {o.statusFormatted || o.status}
                                </div>
                            </div>
                        ))
                    ) : (<p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma obrigação vinculada</p>)}
                </div>

                <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Baixa</h4>
                    {d.discharged ? (
                        <div className="text-sm space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-200">Baixado</span>
                                {d.dischargeDate && <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(d.dischargeDate)}</span>}
                            </div>
                            {d.dischargeValue != null && (
                                <div><span className="text-gray-500 dark:text-gray-400">Valor da baixa:</span> <span className="dark:text-white">{formatCurrency(d.dischargeValue)}</span></div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {!showDischargeForm ? (
                                <button onClick={() => setShowDischargeForm(true)}
                                    className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium">
                                    Registrar baixa
                                </button>
                            ) : (
                                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3 space-y-2">
                                    <TextEdit label="Data da baixa" type="date" value={dischargeDate}
                                        onChange={e => setDischargeDate(e.target.value)} />
                                    <CurrencyInput label="Valor da baixa (opcional)" fractionDigits={2}
                                        value={dischargeValue} onChange={setDischargeValue} />
                                    <div className="flex items-center gap-2">
                                        <button onClick={handleDischarge} disabled={dischargeSubmitting || !dischargeDate}
                                            className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                            {dischargeSubmitting ? 'Salvando...' : 'Confirmar baixa'}
                                        </button>
                                        <button onClick={resetDischargeForm} disabled={dischargeSubmitting}
                                            className="px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg text-sm font-medium">
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={!!selectedInventory}
                onCloseRightPanel={closePanel}
                rightPanel={renderRightPanel()}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Estoque</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <button onClick={() => navigate('/collateral/inventories/new')}
                                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                    <FaPlus size={12} /> Novo
                                </button>
                                <div className="w-96">
                                    <TextEdit label="Buscar descrição, fiel depositário, certificado..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum estoque encontrado</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} ${selectedInventory?.id === row.id ? 'bg-blue-50 dark:bg-slate-700' : ''}`}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm truncate">
                                            <button type="button" onClick={() => handleRowClick(row)}
                                                className="w-full text-left font-medium text-blue-700 dark:text-blue-400 truncate focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                                                title={row.description || row.name || ''}>
                                                {row.description || row.name || '—'}
                                            </button>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                                {row.inventoryTypeFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.legalRegimeFormatted || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 text-right">{row.certifiedValue != null ? formatCurrency(row.certifiedValue) : '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 truncate" title={row.custodyCity || ''}>{row.custodyCity || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 text-center">{row.custodyState || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${row.discharged
                                                ? 'bg-gray-200 text-gray-700 dark:bg-slate-600 dark:text-gray-200'
                                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'}`}>
                                                {row.statusFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400 text-center">{formatDate(row.createdAt)}</td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} onItemsPerPageChange={setPageSize} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ refresh }} />
        </>
    );
};

export default InventoryAssetPageList;
